import { useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useDialog } from '../hooks/useDialog';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Korte uitleg onder de titel; gekoppeld via aria-describedby. */
  description?: ReactNode;
  /** Breedte van het paneel. Standaard 'md'. */
  size?: ModalSize;
  /** Element dat bij openen focus krijgt (anders: autoFocus-kind, dan de sluitknop). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** 'alertdialog' voor bevestigingsvragen. */
  role?: 'dialog' | 'alertdialog';
}

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

/**
 * Basis-dialoog: portal naar body (z-[120]), focus naar binnen + focusval, focus terug bij sluiten,
 * scroll-lock, Escape (alleen de bovenste dialoog) en klik op de achtergrond sluiten.
 */
export function Modal({ title, onClose, children, description, size = 'md', initialFocusRef, role = 'dialog' }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  useDialog({ active: true, onClose, containerRef: panelRef, initialFocusRef });

  return createPortal(
    <div
      className="fixed inset-0 z-[120] grid place-items-center overflow-y-auto bg-slate-950/50 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`w-full ${SIZE_CLASS[size]} rounded-3xl bg-slate-50 p-6 text-slate-900 shadow-xl outline-none dark:border dark:border-white/10 dark:bg-slate-900 dark:text-slate-100`}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 pt-2">
            <h2 id={titleId} className="text-lg font-bold leading-snug">
              {title}
            </h2>
            {description && (
              <div id={descId} className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {description}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-black/5 transition-all duration-150 active:scale-95 dark:bg-slate-800/80"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
